import type {
  AnalyzePrintingsRow,
  AnalyzePrintingsSystem,
  AnalyzePrintingsSystemUser,
} from './types';

interface MachineOption {
  id: number;
  serial: string;
}

/**
 * Sentinel value placed at the top of the Excluded systems dropdown when at
 * least one printing in the result set has no associated system. Matches the
 * legacy hardware-digester behaviour: selecting "N/A" in Excluded systems
 * translates into `excludeNoSystem: true` on the backend request, while the
 * string itself is stripped before the rest of the array is sent.
 */
export const NO_SYSTEM_SENTINEL = 'N/A';

export interface FilterOptions {
  machines: MachineOption[];
  systems: AnalyzePrintingsSystem[];
  systemUsers: AnalyzePrintingsSystemUser[];
  excludedSystemNames: string[];
}

const byId = <T extends { id: number }>(items: T[]): T[] => {
  const map = new Map<number, T>();
  for (const item of items) map.set(item.id, item);
  return [...map.values()];
};

export const deriveFilterOptions = (rows: AnalyzePrintingsRow[]): FilterOptions => {
  const machines = byId(rows.map((r) => r.machine).filter((m): m is MachineOption => !!m)).sort(
    (a, b) => a.serial.localeCompare(b.serial)
  );

  const systemUsers = byId(
    rows.map((r) => r.systemUser).filter((u): u is AnalyzePrintingsSystemUser => !!u)
  ).sort((a, b) => a.username.localeCompare(b.username));

  const systems = byId(
    systemUsers.map((u) => u.system).filter((s): s is AnalyzePrintingsSystem => !!s)
  ).sort((a, b) => a.releaseName.localeCompare(b.releaseName));

  const sortedSystemNames = systems.map((s) => s.releaseName);
  const hasRowWithoutSystem = rows.some((r) => !r.systemUser?.system);

  // Place the "N/A" sentinel at the top of the list when any printing in the
  // result set is system-less, mirroring legacy `putSystemOptionNotApplicableAtTop`.
  const excludedSystemNames = hasRowWithoutSystem
    ? [NO_SYSTEM_SENTINEL, ...sortedSystemNames]
    : sortedSystemNames;

  return {
    machines,
    systems,
    systemUsers,
    excludedSystemNames,
  };
};
